import React, { useState, useMemo, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import {
    AreaConfig,
    ListVendor,
    UnitConfig,
    getScCompoundKeyString,
} from '../../../../models'
import { VendorUnitAreaPricingCard } from '../VendorUnitAreaPricingCard'
import Button from '@mui/material/Button'

interface InputState {
    [compoundKey: string]: number | null
}
interface Props {
    vendor: ListVendor
    unitConfigs: UnitConfig[]
    areaConfigs: AreaConfig[]
    currentScMap: InputState
    vendorScMap: InputState
    handleAcceptNewPricing: () => void
    handleRejectNewPricing: () => void
}

export const VendorSubmission = (props: Props) => {
    const {
        vendor,
        unitConfigs,
        areaConfigs,
        currentScMap,
        vendorScMap,
        handleAcceptNewPricing,
        handleRejectNewPricing,
    } = props
    const theme = useTheme()
    const initialServiceId = vendor.services[0]?.id || null
    const [selectedService, setSelectedService] = useState<number | null>(
        initialServiceId,
    )

    // Add reviewedServices state
    const [reviewedServices, setReviewedServices] = useState<number[]>(
        initialServiceId ? [initialServiceId] : [],
    )

    // Reset selected service when vendor changes
    useEffect(() => {
        const firstServiceId = vendor.services[0]?.id || null
        setSelectedService(firstServiceId)
        setReviewedServices(firstServiceId ? [firstServiceId] : [])
    }, [vendor.id])

    const parsePrice = (value: unknown): number | null => {
        if (typeof value === 'number' && !isNaN(value)) return value
        if (
            typeof value === 'string' &&
            value.trim() !== '' &&
            !isNaN(Number(value))
        )
            return Number(value)
        return null
    }

    // Helper to get area pricing for selected service
    const areaPricing = useMemo(() => {
        if (!selectedService) return []
        return areaConfigs.map((areaConfig) => {
            const key = getScCompoundKeyString({
                serviceId: selectedService,
                vendorId: vendor.id,
                areaConfigId: areaConfig.id,
                unitConfigId: null,
            })
            const currentPrice = parsePrice(currentScMap[key])
            const vendorPrice = parsePrice(vendorScMap[key])
            return {
                id: key,
                area_config: areaConfig,
                currentPrice,
                vendorPrice,
            }
        })
    }, [selectedService, areaConfigs, vendor.id, currentScMap, vendorScMap])

    // Helper to get unit pricing for selected service
    const unitPricing = useMemo(() => {
        if (!selectedService) return []
        return unitConfigs.map((unitConfig) => {
            const key = getScCompoundKeyString({
                serviceId: selectedService,
                vendorId: vendor.id,
                areaConfigId: null,
                unitConfigId: unitConfig.id,
            })
            const currentPrice = parsePrice(currentScMap[key])
            const vendorPrice = parsePrice(vendorScMap[key])
            return {
                id: key,
                unit_config: unitConfig,
                currentPrice,
                vendorPrice,
            }
        })
    }, [selectedService, unitConfigs, vendor.id, currentScMap, vendorScMap])

    // Service list for left panel
    const serviceIdList = useMemo(() => vendor.services.map((srv) => srv.id), [
        vendor.services,
    ])

    // Get service name by id
    const getServiceName = (id: number) => {
        return vendor.services.find((srv) => srv.id === id)?.name || ''
    }

    // Completion indicator (copied from StepVendorPrices)
    const getServiceLabel = (serviceId: number) => {
        const denominator = unitConfigs.length + areaConfigs.length
        if (denominator === 0) {
            return <span style={{ marginRight: 8 }}>(n/a)</span>
        }
        let numerator = 0
        areaConfigs.forEach((ac) => {
            const k = getScCompoundKeyString({
                serviceId: serviceId,
                areaConfigId: ac.id,
                unitConfigId: null,
                vendorId: vendor.id,
            })
            const value = currentScMap[k]
            if (value !== undefined && value !== null && value > 0) {
                numerator += 1
            }
        })
        unitConfigs.forEach((uc) => {
            const k = getScCompoundKeyString({
                serviceId: serviceId,
                areaConfigId: null,
                unitConfigId: uc.id,
                vendorId: vendor.id,
            })
            const value = currentScMap[k]
            if (value !== undefined && value !== null && value > 0) {
                numerator += 1
            }
        })
        if (numerator === denominator) {
            return (
                <CheckCircleOutlineIcon
                    color="primary"
                    fontSize="small"
                    style={{ marginRight: 8 }}
                />
            )
        }
        return (
            <span style={{ marginRight: 8 }}>
                ({numerator}/{denominator})
            </span>
        )
    }

    // Update top service list to also mark as reviewed
    const handleSelectService = (serviceId: number) => {
        setSelectedService(serviceId)
        setReviewedServices((prev: number[]) =>
            prev.includes(serviceId) ? prev : [...prev, serviceId],
        )
    }

    // Restore handleReviewService
    const handleReviewService = (serviceId: number) => {
        setReviewedServices((prev: number[]) =>
            prev.includes(serviceId) ? prev : [...prev, serviceId],
        )
        setSelectedService(serviceId)
    }

    // Accept New Pricing button enabled if all services reviewed
    const allReviewed =
        reviewedServices.length === serviceIdList.length &&
        serviceIdList.length > 0

    if (!selectedService) {
        return null
    }

    return (
        <Box display="flex" flexDirection="column" minHeight={400}>
            {/* Main flex row below header */}
            <Box display="flex" flex={1}>
                {/* Left panel: Services */}
                <Box minWidth={220} borderRight={1} borderColor="divider" p={2}>
                    <Typography fontWeight="bold" mb={2}>
                        Services
                    </Typography>
                    <List>
                        {serviceIdList.map((serviceId) => (
                            <ListItem key={serviceId} disablePadding>
                                <ListItemButton
                                    selected={selectedService === serviceId}
                                    onClick={() =>
                                        handleSelectService(serviceId)
                                    }
                                >
                                    <Typography
                                        fontWeight={
                                            selectedService === serviceId
                                                ? 'bold'
                                                : undefined
                                        }
                                        color={
                                            selectedService === serviceId
                                                ? 'primary'
                                                : undefined
                                        }
                                        sx={{ flex: 1 }}
                                    >
                                        {getServiceName(serviceId)}
                                    </Typography>
                                    {getServiceLabel(serviceId)}
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                    <Typography fontWeight="bold" mb={1}>
                        Review
                    </Typography>
                    <List>
                        {serviceIdList.map((serviceId) => (
                            <ListItem key={serviceId} disablePadding>
                                <ListItemButton
                                    selected={selectedService === serviceId}
                                    onClick={() =>
                                        handleReviewService(serviceId)
                                    }
                                >
                                    {reviewedServices.includes(serviceId) ? (
                                        <CheckCircleOutlineIcon />
                                    ) : (
                                        <RadioButtonUncheckedIcon />
                                    )}
                                    <Typography
                                        fontWeight={
                                            selectedService === serviceId
                                                ? 'bold'
                                                : undefined
                                        }
                                        color={
                                            selectedService === serviceId
                                                ? 'primary'
                                                : undefined
                                        }
                                    >
                                        {getServiceName(serviceId)}
                                    </Typography>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Box display="flex" flexDirection="column" gap={1} mt={2}>
                        <Box>
                            <Button
                                style={{
                                    width: '100%',
                                    padding: '8px 0',
                                    background: allReviewed
                                        ? theme.palette.success.main
                                        : theme.palette.action
                                              .disabledBackground,
                                    color: allReviewed
                                        ? theme.palette.getContrastText(
                                              theme.palette.success.main,
                                          )
                                        : theme.palette.text.disabled,
                                    border: 'none',
                                    borderRadius: 4,
                                    fontWeight: 600,
                                    fontSize: 16,
                                    cursor: allReviewed
                                        ? 'pointer'
                                        : 'not-allowed',
                                }}
                                disabled={!allReviewed}
                                onClick={handleAcceptNewPricing}
                            >
                                Accept New Pricing
                            </Button>
                        </Box>
                        <Box>
                            <Button
                                style={{
                                    width: '100%',
                                    padding: '8px 0',
                                    background: theme.palette.error.main,
                                    color: theme.palette.getContrastText(
                                        theme.palette.error.main,
                                    ),
                                    border: 'none',
                                    borderRadius: 4,
                                    fontWeight: 600,
                                    fontSize: 16,
                                    cursor: 'pointer',
                                }}
                                onClick={handleRejectNewPricing}
                            >
                                Reject
                            </Button>
                        </Box>
                    </Box>
                </Box>
                {/* Main content area */}
                <Box flex={1} p={3}>
                    <Typography fontWeight="bold" fontSize={20} mb={3}>
                        {getServiceName(selectedService)}
                    </Typography>
                    {/* Area (Partial Pricing) */}
                    <Box mb={5}>
                        <Typography fontWeight="bold" mb={1}>
                            Area (Partial Pricing)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {areaPricing.length === 0 ? (
                            <Typography color="text.secondary">
                                No area pricing available.
                            </Typography>
                        ) : (
                            <Box display="flex" flexWrap="wrap" gap={2}>
                                {areaPricing.map((item) => (
                                    <VendorUnitAreaPricingCard
                                        key={item.id}
                                        name={item.area_config?.name || ''}
                                        currentPrice={item.currentPrice}
                                        vendorPrice={item.vendorPrice}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                    {/* Unit Types (Whole Unit Pricing) */}
                    <Box>
                        <Typography fontWeight="bold" mb={1}>
                            Unit Types (Whole Unit Pricing)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {unitPricing.length === 0 ? (
                            <Typography color="text.secondary">
                                No unit pricing available.
                            </Typography>
                        ) : (
                            <Box display="flex" flexWrap="wrap" gap={2}>
                                {unitPricing.map((item) => (
                                    <VendorUnitAreaPricingCard
                                        key={item.id}
                                        name={item.unit_config?.name || ''}
                                        currentPrice={item.currentPrice}
                                        vendorPrice={item.vendorPrice}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
